import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@mui/material';
import { ConfirmModal, ControlledField, MessageGroup, TextareaInputField } from 'remote/global';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ROUTES } from 'utils/data/routes';
import {
  usePostOperatorsUserMessagingGroupsArchiveMutation,
  usePutOperatorsUserMessagingGroupsMutation,
} from 'api/queries/operators';
import { MessageGroupSchemaType, messageGroupSchema } from './types';

type Props = {
  activeGroup: MessageGroup;
  setShowEditGroup: (b: boolean) => void;
};

function GroupEditForm(props: Props) {
  const { activeGroup, setShowEditGroup } = props;
  const tInstance = useTranslation();
  const { t } = tInstance;
  const navigate = useNavigate();
  const [isArchiveGroupModalOpen, setIsArchiveGroupModalOpen] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<MessageGroupSchemaType>({
    defaultValues: {
      description: activeGroup.description,
      name: activeGroup.name,
    },
    resolver: zodResolver(messageGroupSchema),
  });
  const putOperatorsUserMessagingGroupsMutation = usePutOperatorsUserMessagingGroupsMutation(
    String(activeGroup.id)
  );
  const postOperatorsUserMessagingGroupsArchiveMutation =
    usePostOperatorsUserMessagingGroupsArchiveMutation(String(activeGroup.id));

  const onValid = async (data: MessageGroupSchemaType) => {
    await putOperatorsUserMessagingGroupsMutation.mutateAsync(data);
    setShowEditGroup(false);
  };

  const handleArchiveGroup = async () => {
    await postOperatorsUserMessagingGroupsArchiveMutation.mutateAsync();
    toast.success(t('Toast.success'));
    navigate(
      [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.MESSAGES, ROUTES.MessagesRoutes.GROUP].join('/'),
      { replace: true }
    );
  };

  return (
    <>
      <ConfirmModal
        open={isArchiveGroupModalOpen}
        setOpen={setIsArchiveGroupModalOpen}
        onClose={() => setIsArchiveGroupModalOpen(false)}
        onConfirm={() => {
          handleArchiveGroup();
          setIsArchiveGroupModalOpen(false);
        }}
        title={t('modals.archiveGroup.title').toString()}
        description={t('modals.archiveGroup.subtitle').toString()}
      />
      <form onSubmit={handleSubmit(onValid)}>
        <ControlledField control={control} name='name' />
        <ControlledField
          control={control}
          name='description'
          render={(renderProps) => (
            <TextareaInputField
              {...renderProps}
              translationKey='groupDescription'
              fieldProps={{ minRows: 4 }}
            />
          )}
        />

        <Grid container xs={12} gap={1} justifyContent='space-between' mt={2}>
          <Button type='button' onClick={() => setIsArchiveGroupModalOpen(true)} variant='outlined'>
            {t('delete')}
          </Button>
          <Button
            disabled={isSubmitting || isValidating || isLoading}
            type='submit'
            variant='contained'
          >
            {t('save')}
          </Button>
        </Grid>
      </form>
    </>
  );
}

export default GroupEditForm;
